import { useLazyQuery } from '@apollo/client';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SearchInputIcon } from '../../assets';
import { CATEGORY_OPTIONS } from '../../constants/brandOptions';
import { GET_BRANDS } from '../../graphql/brands/query';
import { GET_PROSPECTS } from '../../graphql/prospects/query';
import { BrandImage } from '../images/BrandImage';
import { GenericDropdown } from './GenericDropdown';

export type ClientBrandItem = {
  id: string;
  name: string;
  profile_picture?: string;
  is_prospect?: boolean;
};

type ResBrands = {
  brands: {
    data: ClientBrandItem[];
  };
};

type Props = {
  /** When true, lists both clients AND prospects (for Proposals). Default: false */
  includeProspects?: boolean;
  selectedBrand: ClientBrandItem | null;
  onSelect: (brand: ClientBrandItem) => void;
  /** react-hook-form control — needed for the category GenericDropdown */
  control: Control<any>;
  /** react-hook-form field name for the category filter. Default: '__clientCategory' */
  categoryFieldName?: string;
};

/**
 * Imperative handle exposed via `ref`. Lets parent flows (e.g. CreateCampaign)
 * hook into the inline brand-creation UX that mirrors the influencer
 * inline-creation flow used by `UserDropdownList.tsx`:
 *   1. Parent opens `BrandPopUp` (redirect=false) next to the read-only client
 *      input.
 *   2. On save, `BrandPopUp` fires `setCreatedBrand(id)`.
 *   3. Parent calls `ref.current.onBrandCreated(id)` — this triggers a refetch
 *      of the brand list AND starts tracking the new id so that when the
 *      refetched list contains the new brand, it is auto-selected via
 *      `onSelect`.
 */
export type ClientSearchPanelHandle = {
  onBrandCreated: (id: string) => void;
};

export const ClientSearchPanel = forwardRef<ClientSearchPanelHandle, Props>(
  function ClientSearchPanel(
    {
      includeProspects = false,
      selectedBrand,
      onSelect,
      control,
      categoryFieldName = '__clientCategory',
    },
    ref,
  ) {
    const { t } = useTranslation('common');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [brands, setBrands] = useState<ClientBrandItem[]>([]);
    const [prospects, setProspects] = useState<ClientBrandItem[]>([]);
    const [createdBrandId, setCreatedBrandId] = useState<string | null>(null);

    const vars = {
      name: searchQuery || undefined,
      limit: 100,
      offset: 0,
      category: selectedCategory ? [selectedCategory] : undefined,
    };

    const [fetchBrands] = useLazyQuery<ResBrands>(GET_BRANDS, {
      onCompleted: (data) => setBrands(data?.brands?.data || []),
    });

    const [fetchProspects] = useLazyQuery<ResBrands>(GET_PROSPECTS, {
      onCompleted: (data) =>
        setProspects(
          (data?.brands?.data || []).map((p) => ({ ...p, is_prospect: true })),
        ),
    });

    useEffect(() => {
      fetchBrands({ variables: vars });
      if (includeProspects) {
        fetchProspects({ variables: vars });
      }
    }, [searchQuery, selectedCategory]);

    // Clients first, then prospects
    const items: ClientBrandItem[] = includeProspects
      ? [
          ...brands.map((b) => ({ ...b, is_prospect: false as const })),
          ...prospects,
        ]
      : brands;

    // Expose the inline-creation hook for parent flows (e.g. CreateCampaign).
    // `searchQuery`/`selectedCategory` are deps so the closure always uses the
    // current `vars` when triggering the refetch.
    useImperativeHandle(
      ref,
      () => ({
        onBrandCreated: (id: string) => {
          setCreatedBrandId(id);
          fetchBrands({ variables: vars });
        },
      }),
      [searchQuery, selectedCategory],
    );

    // When a brand is just created, wait until the refetched list contains
    // it, then auto-select via `onSelect`. Mirrors how `UserDropdownList`
    // watches `createdUser` + the options list to fill empty fields.
    useEffect(() => {
      if (!createdBrandId) return;
      const newBrand = brands.find((b) => b.id === createdBrandId);
      if (newBrand) {
        setCreatedBrandId(null);
        onSelect(newBrand);
      }
    }, [createdBrandId, brands, onSelect]);

    return (
      <div className="flex flex-col gap-y-3 w-1/2">
        <p className="text-white font-semibold text-sm">
          {t('proposals.createClient')}
        </p>

        {/* Search input */}
        <div className="relative flex items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('global.search')}
            className="block h-[41px] w-full border-none pr-3 pl-9 py-[8px] text-sm font-medium text-dark-blue placeholder:text-[#5A5A5A] focus:outline-none focus:ring-0 rounded-[4px] bg-white"
            autoComplete="off"
          />
          <div className="absolute left-3 pointer-events-none">
            <SearchInputIcon />
          </div>
        </div>

        {/* Category filter */}
        <GenericDropdown
          control={control}
          name={categoryFieldName}
          isClearable={true}
          placeholder={t('singleBrand.category')}
          options={CATEGORY_OPTIONS.map((cat) => ({
            value: cat,
            label: t(`enums.category.${cat}`),
          }))}
          onChangeManual={(val: { value: string; label: string } | null) =>
            setSelectedCategory(val?.value || '')
          }
        />

        {/* Brand/Prospect list */}
        <div className="overflow-y-auto pr-1 max-h-[260px] customScroll">
          {items.map((brand) => (
            <button
              key={brand.id}
              type="button"
              onClick={() => onSelect(brand)}
              className={`flex flex-row items-center gap-x-3 px-3 py-2 rounded-[8px] text-left transition-colors w-full ${
                selectedBrand?.id === brand.id
                  ? 'bg-influentia-light-blue text-dark-blue'
                  : 'hover:bg-dark-blue text-white'
              }`}
            >
              <BrandImage image={brand.profile_picture} size="size-[38px]" />
              <span className="text-sm font-medium truncate flex-1">
                {brand.name}
              </span>
              {includeProspects && brand.is_prospect && (
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                    selectedBrand?.id === brand.id
                      ? 'bg-dark-blue text-white'
                      : 'bg-influentia-light-blue text-dark-blue'
                  }`}
                >
                  {t('global.prospect')}
                </span>
              )}
            </button>
          ))}
          {items.length === 0 && (
            <p className="text-gray-400 text-sm italic px-3 py-2">
              {t('global.no-data')}
            </p>
          )}
        </div>
      </div>
    );
  },
);
